import { ROBOT_COLOURS, seatColour } from '@roborally/engine';
import type { PlayerRow } from './api.js';

/**
 * What each robot is called and what colour it is drawn in.
 *
 * Shared by the live board and the replay: two screens showing the same
 * game must not disagree about which robot is the red one.
 */

/** What the colour picker calls each colour. */
export const COLOUR_NAMES: Record<string, string> = {
  '#d9503f': 'Red', '#3d86ad': 'Blue', '#f2c94c': 'Yellow', '#57a06a': 'Green',
  '#9a70c4': 'Purple', '#e5842c': 'Orange', '#4b5058': 'Black', '#d5dbcf': 'White',
};

/** The colours nobody at the table has yet, in palette order. */
export function freeColours(players: PlayerRow[]): string[] {
  const taken = new Set(players.map((p) => p.colour ?? seatColour(p.seat)));
  return ROBOT_COLOURS.filter((c) => !taken.has(c));
}

/**
 * What to call each robot on screen: the player's own name.
 *
 * `robot1` is an internal identity -- it is how the engine, the database
 * and every authorisation check refer to a seat -- and there is no reason
 * to show it to anyone. Callers fall back to the id only when the player
 * list has not loaded or a robot has no player.
 */
export function nameMap(players: PlayerRow[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const p of players) map.set(p.robot_id, p.display_name);
  return map;
}

/** The robots a bot account is playing. */
export function botSet(players: PlayerRow[]): Set<string> {
  return new Set(players.filter((p) => p.is_bot === 1).map((p) => p.robot_id));
}

/**
 * The colour the player picked on joining, and where the row names none,
 * the SEAT's -- never an index into whichever list happened to be
 * available, which let two players see the same robot in different
 * colours. Both are fixed for the whole game and identical on every
 * client, so everyone sees the same board.
 *
 * Robot ids are `robot<seat>`, so the seat is recoverable even before the
 * player list has loaded.
 */
export function colourMap(
  players: PlayerRow[], robots: { id: string }[],
): Map<string, string> {
  const map = new Map<string, string>();

  const seatOf = (robotId: string) => {
    const fromPlayers = players.find((p) => p.robot_id === robotId)?.seat;
    if (fromPlayers !== undefined) return fromPlayers;
    const digits = robotId.match(/(\d+)$/);
    return digits ? Number(digits[1]) : 1;
  };

  for (const p of players) {
    map.set(p.robot_id, p.colour ?? seatColour(p.seat));
  }
  for (const r of robots) {
    if (!map.has(r.id)) map.set(r.id, seatColour(seatOf(r.id)));
  }
  return map;
}
